import {
  type DeliveryChannel,
  DeliveryChannelType,
  type EmailDeliveryChannel,
  type SlackDeliveryChannel,
  SlackDeliveryTarget,
  type TeamsDeliveryChannel,
  TeamsDeliveryTarget,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';

type DeliveryFormState = {
  emailEnabled: boolean;
  emailRecipients: string[];
  slackEnabled: boolean;
  slackTarget: SlackDeliveryTarget;
  slackChannelId: string | null;
  slackChannelName: string | null;
  teamsEnabled: boolean;
  teamsTarget: TeamsDeliveryTarget;
  teamsTeamId: string | null;
  teamsChannelId: string | null;
  teamsChannelName: string | null;
};

type BuildSlackChannelInput = {
  slackEnabled: boolean;
  slackTarget: SlackDeliveryTarget;
  slackTeamId: string | null;
  slackSelfUserId: string | null;
  slackChannelId: string | null;
  slackChannelName: string | null;
  userId: string | null;
};

type BuildTeamsChannelInput = {
  teamsEnabled: boolean;
  teamsTarget: TeamsDeliveryTarget;
  teamsTenantId: string | null;
  teamsSelfAadObjectId: string | null;
  teamsTeamId: string | null;
  teamsChannelId: string | null;
  teamsChannelName: string | null;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates a single email address using a basic well-formedness check.
 *
 * @param {string} value - The candidate email
 * @returns {boolean} True when the string parses as `local@domain.tld`
 */
function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

/**
 * Trims and de-duplicates a list of email recipient strings.
 *
 * @param {string[]} recipients - Raw recipient strings from the TagsInput
 * @returns {string[]} Trimmed, deduped, non-empty recipient strings
 */
function normalizeRecipients(recipients: string[]): string[] {
  return [...new Set(recipients.map((email) => email.trim()).filter(Boolean))];
}

/**
 * Picks the email channel out of a workflow's delivery_channels array.
 *
 * @param {DeliveryChannel[]} channels - The persisted channels array
 * @returns {EmailDeliveryChannel | undefined} The email channel or undefined
 */
function findEmailChannel(channels: DeliveryChannel[]): EmailDeliveryChannel | undefined {
  return channels.find((channel): channel is EmailDeliveryChannel => channel.type === DeliveryChannelType.EMAIL);
}

/**
 * Picks the Slack channel out of a workflow's delivery_channels array.
 *
 * @param {DeliveryChannel[]} channels - The persisted channels array
 * @returns {SlackDeliveryChannel | undefined} The Slack channel or undefined
 */
function findSlackChannel(channels: DeliveryChannel[]): SlackDeliveryChannel | undefined {
  return channels.find((channel): channel is SlackDeliveryChannel => channel.type === DeliveryChannelType.SLACK);
}

/**
 * Picks the Teams channel out of a workflow's delivery_channels array.
 *
 * @param {DeliveryChannel[]} channels - The persisted channels array
 * @returns {TeamsDeliveryChannel | undefined} The Teams channel or undefined
 */
function findTeamsChannel(channels: DeliveryChannel[]): TeamsDeliveryChannel | undefined {
  return channels.find((channel): channel is TeamsDeliveryChannel => channel.type === DeliveryChannelType.TEAMS);
}

/**
 * Derives the picker's local form state from a persisted DeliveryChannel array.
 *
 * Responsibilities:
 * 1. Default to no destination selected when no channels are configured
 * 2. Pick the persisted method with precedence Teams → Slack → Email (only one
 *    method is ever persisted; precedence is defensive against legacy multi-channel rows)
 * 3. Surface email recipients (or fall back to the current user's email) when the email channel is absent
 *
 * @param {DeliveryChannel[]} channels - The persisted channels (may be empty)
 * @param {string} fallbackEmail - The current user's email to seed recipients with when no email channel exists
 * @returns {DeliveryFormState} Initial form state for the picker
 */
function channelsToFormState(channels: DeliveryChannel[], fallbackEmail: string): DeliveryFormState {
  const email = findEmailChannel(channels);
  const slack = findSlackChannel(channels);
  const teams = findTeamsChannel(channels);
  const emailRecipients = email?.recipients ?? (fallbackEmail ? [fallbackEmail] : []);

  // Slack defaults — overridden only when a Slack channel is persisted.
  const slackEnabled = !!slack && !teams;
  const slackTarget = slack?.target ?? SlackDeliveryTarget.DM;
  const slackChannelId = slack?.target === SlackDeliveryTarget.CHANNEL ? slack.channel_id : null;
  const slackChannelName = slack?.target === SlackDeliveryTarget.CHANNEL ? (slack.channel_name ?? null) : null;

  // Teams defaults — overridden only when a Teams channel is persisted.
  const teamsEnabled = !!teams;
  const teamsTarget = teams?.target ?? TeamsDeliveryTarget.CHAT;
  const teamsTeamId = teams?.target === TeamsDeliveryTarget.CHANNEL ? teams.team_id : null;
  const teamsChannelId = teams?.target === TeamsDeliveryTarget.CHANNEL ? teams.channel_id : null;
  const teamsChannelName = teams?.target === TeamsDeliveryTarget.CHANNEL ? (teams.channel_name ?? null) : null;

  return {
    // A fresh automation starts with no destination selected so the user has to
    // make an explicit choice (Email vs Slack vs Teams) rather than silently
    // committing to email-the-current-user. Saved workflows keep whatever they
    // were configured with.
    emailEnabled: !!email && !slack && !teams,
    emailRecipients,
    slackEnabled,
    slackTarget,
    slackChannelId,
    slackChannelName,
    teamsEnabled,
    teamsTarget,
    teamsTeamId,
    teamsChannelId,
    teamsChannelName,
  };
}

/**
 * Builds a Slack delivery entry from the picker's current state, returning null when required fields are missing.
 *
 * @param {BuildSlackChannelInput} input - Slack picker state plus the resolved Slack team/user ids
 * @returns {SlackDeliveryChannel | null} The Slack channel entry or null when incomplete
 */
function buildSlackChannel(input: BuildSlackChannelInput): SlackDeliveryChannel | null {
  if (!input.slackEnabled || !input.slackTeamId) return null;
  if (input.slackTarget === SlackDeliveryTarget.DM) {
    if (!input.slackSelfUserId) return null;
    return {
      type: DeliveryChannelType.SLACK,
      target: SlackDeliveryTarget.DM,
      team_id: input.slackTeamId,
      slack_user_id: input.slackSelfUserId,
      user_id: input.userId ?? undefined,
    };
  }
  if (input.slackTarget === SlackDeliveryTarget.CHANNEL) {
    if (!input.slackChannelId) return null;
    return {
      type: DeliveryChannelType.SLACK,
      target: SlackDeliveryTarget.CHANNEL,
      team_id: input.slackTeamId,
      channel_id: input.slackChannelId,
      channel_name: input.slackChannelName ?? undefined,
    };
  }
  return null;
}

/**
 * Builds a Teams delivery entry from the picker's current state, returning null when required fields are missing.
 *
 * `tenant_id` and the self `aad_object_id` come from the Teams status query;
 * `team_id`/`channel_id` come from the selected channel (each Teams channel
 * carries its own team, unlike Slack's single workspace).
 *
 * @param {BuildTeamsChannelInput} input - Teams picker state plus the resolved tenant/self identity
 * @returns {TeamsDeliveryChannel | null} The Teams channel entry or null when incomplete
 */
function buildTeamsChannel(input: BuildTeamsChannelInput): TeamsDeliveryChannel | null {
  if (!input.teamsEnabled || !input.teamsTenantId) return null;
  if (input.teamsTarget === TeamsDeliveryTarget.CHAT) {
    if (!input.teamsSelfAadObjectId) return null;
    return {
      type: DeliveryChannelType.TEAMS,
      target: TeamsDeliveryTarget.CHAT,
      tenant_id: input.teamsTenantId,
      aad_object_id: input.teamsSelfAadObjectId,
    };
  }
  if (input.teamsTarget === TeamsDeliveryTarget.CHANNEL) {
    if (!input.teamsTeamId || !input.teamsChannelId) return null;
    return {
      type: DeliveryChannelType.TEAMS,
      target: TeamsDeliveryTarget.CHANNEL,
      tenant_id: input.teamsTenantId,
      team_id: input.teamsTeamId,
      channel_id: input.teamsChannelId,
      channel_name: input.teamsChannelName ?? undefined,
    };
  }
  return null;
}

export {
  buildSlackChannel,
  buildTeamsChannel,
  channelsToFormState,
  findTeamsChannel,
  isValidEmail,
  normalizeRecipients,
};
